<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { useAuthStore } from '@/stores/auth.store';
import { useChatUnreadStore } from '@/stores/chat.store';
import { extractApiError } from '@/api/client';
import * as chatApi from '@/api/chat';
import { useChatHub } from '@/composables/useChatHub';
import { useIsMobile } from '@/composables/useIsMobile';
import type { ChatMessageModel, ChatRoomModel } from '@/types/chat';
import { formatRelativeDate, roomTypeIcon } from '@/types/chat';

const auth = useAuthStore();
const chatUnreadStore = useChatUnreadStore();
const isMobile = useIsMobile();
// Mobile (<768px) : navigation mono-volet (liste des conversations <-> fil).
const mobileThreadOpen = ref(false);
function openRoom(roomId: number): void {
  mobileThreadOpen.value = true;
  void selectRoom(roomId);
}
function backToRooms(): void { mobileThreadOpen.value = false; }
const hub = useChatHub();
const rooms = ref<ChatRoomModel[]>([]);
const activeRoomId = ref<number | null>(null);
const messages = ref<ChatMessageModel[]>([]);
const draft = ref('');
const loadingRooms = ref(false);
const loadingMessages = ref(false);
const sending = ref(false);
const messagesContainer = ref<HTMLElement | null>(null);

const unsubscribers: Array<() => void> = [];

const activeRoom = computed(
  () => rooms.value.find((r) => r.id === activeRoomId.value) ?? null,
);

const orderedRooms = computed(() =>
  [...rooms.value].sort((a, b) => {
    if (a.unreadCount !== b.unreadCount) return b.unreadCount - a.unreadCount;
    const tsA = a.lastMessage?.sentAt ?? '';
    const tsB = b.lastMessage?.sentAt ?? '';
    return tsB.localeCompare(tsA);
  }),
);

async function loadRooms(): Promise<void> {
  loadingRooms.value = true;
  try {
    rooms.value = await chatApi.fetchRooms();
    if (activeRoomId.value == null && rooms.value.length > 0) {
      activeRoomId.value = rooms.value[0].id;
    }
  } catch (err) {
    ElMessage.error(extractApiError(err));
  } finally {
    loadingRooms.value = false;
  }
}

async function loadMessages(roomId: number, scrollToBottom = true): Promise<void> {
  loadingMessages.value = true;
  try {
    const list = await chatApi.fetchMessages(roomId, undefined, 50);
    // Le backend renvoie les plus recents en premier ; on inverse pour affichage chronologique.
    messages.value = [...list].sort((a, b) => a.sentAt.localeCompare(b.sentAt));
    if (scrollToBottom) await scrollToEnd();
  } catch (err) {
    ElMessage.error(extractApiError(err));
    messages.value = [];
  } finally {
    loadingMessages.value = false;
  }
}

async function scrollToEnd(): Promise<void> {
  await nextTick();
  const el = messagesContainer.value;
  if (el) el.scrollTop = el.scrollHeight;
}

async function selectRoom(roomId: number): Promise<void> {
  const previousId = activeRoomId.value;
  if (previousId === roomId) return;
  if (previousId !== null) {
    try { await hub.leaveRoom(previousId); } catch { /* hub peut etre offline */ }
  }
  // L'affectation declenche le watch -> loadMessages.
  activeRoomId.value = roomId;
  try { await hub.joinRoom(roomId); } catch { /* hub peut etre offline */ }
  try {
    await chatApi.markRoomAsRead(roomId);
    await hub.markAsRead(roomId);
    const room = rooms.value.find((r) => r.id === roomId);
    if (room) room.unreadCount = 0;
    // Refresh du badge global pour reflet immediat (sinon poll 45s).
    void chatUnreadStore.refresh();
  } catch {
    // non bloquant
  }
}

async function onSend(): Promise<void> {
  if (!activeRoomId.value || !draft.value.trim()) return;
  sending.value = true;
  const content = draft.value.trim();
  try {
    if (hub.isConnected.value) {
      // Le hub broadcast a tous (y compris l'emetteur via ReceiveMessage).
      await hub.sendMessage(activeRoomId.value, content, null);
    } else {
      // Fallback REST si le hub est down.
      const sent = await chatApi.sendMessage({
        chatRoomId: activeRoomId.value,
        content,
      });
      mergeIncomingMessage(sent);
    }
    draft.value = '';
  } catch (err) {
    ElMessage.error(extractApiError(err));
  } finally {
    sending.value = false;
  }
}

function mergeIncomingMessage(msg: ChatMessageModel): void {
  if (messages.value.some((m) => m.id === msg.id)) return;
  messages.value.push(msg);
  // Tri defensif au cas ou un message arrive hors ordre.
  messages.value.sort((a, b) => a.sentAt.localeCompare(b.sentAt));
  void scrollToEnd();
}

function bumpRoomLastMessage(roomId: number, msg: ChatMessageModel): void {
  const room = rooms.value.find((r) => r.id === roomId);
  if (!room) return;
  room.lastMessage = msg;
  if (roomId !== activeRoomId.value) {
    room.unreadCount = (room.unreadCount ?? 0) + 1;
  }
}

function setupHubListeners(): void {
  unsubscribers.push(
    hub.onMessageReceived((roomId, msg) => {
      bumpRoomLastMessage(roomId, msg);
      if (roomId === activeRoomId.value) {
        mergeIncomingMessage(msg);
        // Auto-ack des messages recus dans la room ouverte.
        void hub.markAsRead(roomId);
      }
    }),
    hub.onMessageEdited((messageId, content) => {
      const m = messages.value.find((x) => x.id === messageId);
      if (m) {
        m.content = content;
        m.editedAt = new Date().toISOString();
      }
    }),
    hub.onMessageDeleted((messageId) => {
      const m = messages.value.find((x) => x.id === messageId);
      if (m) m.isDeleted = true;
    }),
    hub.onUserOnline(() => { /* state global maj via hub */ }),
    hub.onUserOffline(() => { /* idem */ }),
  );
}

onMounted(async () => {
  await loadRooms();
  // loadRooms a possiblement set activeRoomId (1ere room) -> le watch va load messages.
  setupHubListeners();
  await hub.connect();
  if (activeRoomId.value) {
    try { await hub.joinRoom(activeRoomId.value); } catch { /* hub offline */ }
  }
  await hub.refreshOnlineUsers();
});

onBeforeUnmount(async () => {
  unsubscribers.forEach((fn) => fn());
  unsubscribers.length = 0;
  if (activeRoomId.value !== null) {
    try { await hub.leaveRoom(activeRoomId.value); } catch { /* ignore */ }
  }
  await hub.disconnect();
});

watch(activeRoomId, async (id, prev) => {
  if (id !== null && id !== prev) {
    await loadMessages(id);
  }
});

function isMine(msg: ChatMessageModel): boolean {
  return auth.user?.id != null && msg.senderId === auth.user.id;
}
</script>

<template>
  <div class="chat-view" :class="{ 'chat-view--mobile': isMobile }">
    <aside v-show="!isMobile || !mobileThreadOpen" class="chat-rooms" v-loading="loadingRooms">
      <header class="chat-rooms-header">
        <h2 class="chat-rooms-title">Conversations</h2>
        <el-button :icon="'Refresh'" size="small" text @click="loadRooms" />
      </header>
      <el-empty v-if="!orderedRooms.length" description="Aucune conversation. Demarrez-en via le profil d'un joueur." />
      <ul v-else class="chat-rooms-list">
        <li
          v-for="r in orderedRooms"
          :key="r.id"
          class="chat-room"
          :class="{ active: r.id === activeRoomId, unread: r.unreadCount > 0 }"
          @click="openRoom(r.id)"
        >
          <el-icon><component :is="roomTypeIcon(r.roomType)" /></el-icon>
          <div class="chat-room-body">
            <div class="chat-room-name">{{ r.displayName || r.name || `Room #${r.id}` }}</div>
            <div v-if="r.lastMessage" class="chat-room-last text-muted">
              {{ r.lastMessage.senderName }} : {{ r.lastMessage.content.slice(0, 60) }}
            </div>
          </div>
          <el-badge v-if="r.unreadCount > 0" :value="r.unreadCount" class="chat-room-badge" />
        </li>
      </ul>
    </aside>

    <section v-show="!isMobile || mobileThreadOpen" class="chat-pane" v-loading="loadingMessages">
      <header class="chat-pane-header" v-if="activeRoom">
        <el-button
          v-if="isMobile"
          class="chat-back"
          size="small"
          text
          @click="backToRooms"
        >
          ← Retour
        </el-button>
        <el-icon><component :is="roomTypeIcon(activeRoom.roomType)" /></el-icon>
        <h2 class="chat-pane-title">
          {{ activeRoom.displayName || activeRoom.name || `Room #${activeRoom.id}` }}
        </h2>
        <span class="text-muted">{{ activeRoom.participants.length }} participant(s)</span>
      </header>

      <div v-if="!activeRoom" class="chat-pane-empty">
        <el-empty description="Selectionnez une conversation a gauche." />
      </div>

      <div v-else ref="messagesContainer" class="chat-messages">
        <div
          v-for="msg in messages"
          :key="msg.id"
          class="chat-msg"
          :class="{ mine: isMine(msg), system: msg.messageType === 'System' }"
        >
          <div v-if="!isMine(msg) && msg.messageType !== 'System'" class="chat-msg-sender">
            {{ msg.senderDisplayName || msg.senderName }}
          </div>
          <div class="chat-msg-bubble">
            <span v-if="msg.isDeleted" class="chat-msg-deleted text-muted">[Message supprime]</span>
            <span v-else>{{ msg.content }}</span>
          </div>
          <div class="chat-msg-meta text-muted">
            {{ formatRelativeDate(msg.sentAt) }}
            <span v-if="msg.editedAt"> · edite</span>
          </div>
        </div>
      </div>

      <footer v-if="activeRoom" class="chat-input">
        <el-input
          v-model="draft"
          type="textarea"
          :rows="2"
          :maxlength="2000"
          placeholder="Votre message..."
          resize="none"
          @keydown.enter.exact.prevent="onSend"
        />
        <el-button
          type="primary"
          :icon="'Promotion'"
          :loading="sending"
          :disabled="!draft.trim()"
          @click="onSend"
        >
          Envoyer
        </el-button>
      </footer>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.chat-view {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: $space-4;
  height: calc(100vh - 160px);
  max-width: $layout-max-width;

  // Mobile (<768px) : mono-volet (liste OU fil via v-show) sur toute la largeur.
  @media (max-width: 767.99px) {
    grid-template-columns: 1fr;
    height: calc(100dvh - 200px);
  }
}
.chat-back { flex: 0 0 auto; }

.chat-rooms {
  @include card-dark;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-rooms-header {
  @include flex-between;
  padding: $space-3 $space-4;
  border-bottom: 1px solid $border-default;
}

.chat-rooms-title {
  @include section-title;
}

.chat-rooms-list {
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  @include scrollbar-thin;
}

.chat-room {
  display: flex;
  align-items: center;
  gap: $space-2;
  padding: $space-3 $space-4;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  transition: background $duration-fast;

  &:hover { background: $bg-tertiary; }
  &.active {
    background: rgba($accent-primary, 0.15);
    border-left: 3px solid $accent-primary;
  }
  &.unread .chat-room-name {
    font-weight: $font-weight-semibold;
    color: $color-neon-cyan;
  }
}

.chat-room-body {
  flex: 1;
  min-width: 0;
}

.chat-room-name {
  font-size: $font-size-sm;
  color: $text-primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-room-last {
  font-size: $font-size-xs;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-pane {
  @include card-dark;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-pane-header {
  display: flex;
  align-items: center;
  gap: $space-2;
  padding: $space-3 $space-4;
  border-bottom: 1px solid $border-default;
}

.chat-pane-title {
  @include section-title;
  flex: 1;
}

.chat-pane-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: $space-4;
  display: flex;
  flex-direction: column;
  gap: $space-2;
  @include scrollbar-thin;
}

.chat-msg {
  max-width: 70%;
  display: flex;
  flex-direction: column;
  gap: 2px;

  &.mine {
    align-self: flex-end;
    .chat-msg-bubble {
      background: rgba($accent-primary, 0.20);
      border-color: $accent-primary;
    }
    .chat-msg-meta { text-align: right; }
  }
  &.system {
    align-self: center;
    .chat-msg-bubble {
      background: transparent;
      border: 1px dashed $border-default;
      font-style: italic;
      color: $text-muted;
    }
  }
}

.chat-msg-sender {
  font-size: $font-size-xs;
  color: $color-neon-cyan;
  font-weight: $font-weight-medium;
}

.chat-msg-bubble {
  background: $bg-tertiary;
  border: 1px solid $border-default;
  border-radius: 6px;
  padding: $space-2 $space-3;
  word-break: break-word;
  white-space: pre-wrap;
}

.chat-msg-deleted { font-style: italic; }

.chat-msg-meta {
  font-size: 10px;
}

.chat-input {
  display: flex;
  gap: $space-2;
  padding: $space-3 $space-4;
  border-top: 1px solid $border-default;
  align-items: flex-end;

  :deep(.el-textarea) { flex: 1; }
}
</style>
